<template>
  <div v-if="hasVehicles" :class="['base-vehicle-selector']">
    <div v-click-outside="resetQuickSearch" class="base-vehicle-selector__quick-search">
      <oie-base-input
        v-model="searchQuery"
        :tooltip="tStatic('searchModelTooltip')"
        type="text"
        :label-text="tStatic('searchModel')"
        @keydown.enter.prevent></oie-base-input>
      <ul class="base-vehicle-selector__quick-search-list" role="listbox">
        <li
          v-for="model in entities.models.data"
          :key="model.code"
          role="option"
          tabindex="0"
          class="dropdown__option"
          @click="setVehicleSelectionByCode(model.code)"
          @keypress.enter="setVehicleSelectionByCode(model.code)">
          {{ model.vehicleType }} / {{ stripHtml(model.name) }}
        </li>
      </ul>
    </div>
    <p>{{ tStatic('selectVehicleText') }}</p>
    <oie-base-dropdown
      v-model="selectedType"
      :initial-text="tStatic('filterSelectVehicleType')"
      :tooltip="tStatic('filterSelectVehicleTypeTooltip')"
      :options="types"
      :is-large="false"
      :use-full-width="true"
      :enable-add-option="true"
      @add-option="showModal($event, 'type')"></oie-base-dropdown>

    <oie-base-dropdown
      v-model="selectedBrand"
      :initial-text="tStatic('filterSelectVehicleBrand')"
      :tooltip="tStatic('filterSelectVehicleBrandTooltip')"
      :options="brands"
      :is-large="false"
      :is-disabled="!selectedType"
      :use-wild-card-search="true"
      :use-full-width="true"
      :enable-add-option="true"
      @add-option="showModal($event, 'brand')"></oie-base-dropdown>

    <oie-base-dropdown
      v-model="selectedModel"
      :initial-text="tStatic('filterSelectVehicleModel')"
      :tooltip="tStatic('filterSelectVehicleModelTooltip')"
      :options="models"
      :is-large="false"
      :is-disabled="!selectedBrand"
      :use-full-width="true"
      :use-wild-card-search="true"
      :enable-add-option="true"
      @add-option="showModal($event, 'model')"></oie-base-dropdown>
  </div>
</template>

<script>
import _ from 'lodash'
import search from '@/mixins/search'
import httpService from '../../core/http-service'
import { mapActions } from 'vuex'

export default {
  mixins: [search],
  props: {
    vehicles: {
      type: Array,
      default: () => []
    },
    preSelectedVehicle: {
      type: [String, Object],
      default: null
    }
  },
  data() {
    return {
      selectedType: null,
      selectedBrand: null,
      selectedModel: null
    }
  },
  computed: {
    hasVehicles() { return this.vehicles.length > 0 },
    types() {
      return this.vehicles.map(v => ({ text: v.name, value: v.id }))
    },
    brands() {
      const type = this.vehicles.find(v => v.id === this.selectedType)
      if (!type) { return [] }
      return type.brands.map(b => ({ text: b.name, value: b.id }))
    },
    models() {
      const type = this.vehicles.find(v => v.id === this.selectedType)
      if (!type) { return [] }
      const brand = type.brands.find(b => b.id === this.selectedBrand)
      if (!brand) { return [] }
      return brand.models.map(m => ({ text: m.name, value: m.id }))
    }
  },
  watch: {
    searchQuery: _.debounce(async function () {
      if(this.searchQuery.length > 2) {
        await this.searchModels()
      }
    }, 400),
    selectedType(newVal) {
      this.selectedBrand = null
      this.selectedModel = null
      this.$emit('update:selectedType', newVal)
    },
    selectedBrand(newVal) {
      this.selectedModel = null
      this.$emit('update:selectedBrand', newVal)
    },
    selectedModel(newVal, oldVal) {
      this.$emit('update:selectedModel', newVal)
    }
  },
  async mounted() {
    if(this.preSelectedVehicle) {
      if(_.isString(this.preSelectedVehicle)) {
        this.setVehicleSelectionByCode(this.preSelectedVehicle)
      } else {
        this.selectedType = this.preSelectedVehicle.type
        await this.$nextTick()
        this.selectedBrand = this.preSelectedVehicle.brand
        await this.$nextTick()
        this.selectedModel = this.preSelectedVehicle.model
      }
    }

    this.subscribe('reset-base-vehicle-selector', () => {
      this.selectedType = null
    })
  },
  methods: {
    ...mapActions('myMachines', ['updateVehicles']),
    ...mapActions('ui', ['addNotification']),
    async setVehicleSelectionByCode(code) {
      const models = _.chain(this.vehicles).flatMap(p => p.brands).flatMap(b => b.models).value()
      const selected = models.find(p => p.id === code)
      if (!selected) return
      this.selectedType = selected.vehicleTypeId
      await this.$nextTick()
      this.selectedBrand = selected.brandId
      await this.$nextTick()
      this.selectedModel = selected.id
      this.resetQuickSearch()
    },
    resetQuickSearch() {
      this.clearSearchResults()
      this.searchQuery = ''
    },
    showModal($event, type) {
      this.publish('modal.show.add-option', {
        header: this.tStatic('addNewOptionHeader'),
        text: this.tStatic('addNewOptionDescription'),
        initialValue: $event,
        type: type,
        action:
        {
          text: this.tStatic('addNewOptionText'),
          handler: this.handleAddOption
        }
      })
    },
    async handleAddOption(newOption, type) {
      let errorText = ''
      switch(type) {
        case 'type':
          const existingType = _.find(this.types, type => type.text.toLowerCase() === newOption.toLowerCase())
          if(!existingType) {
            const response = await httpService.put('my-machines/machine-models', { machineType: type, name: newOption, parentId: null })
            const newVehicleTree = response.data
            const newType = _.find(newVehicleTree, obj => obj.name === newOption)
            if(newType) {
              this.selectedType = newType.id
              this.updateVehicles(newVehicleTree)
            }
          } else {
            errorText = 'This option already exists'
          }
          break
        case 'brand':
          if(this.selectedType) {
            const existingBrand = _.find(this.brands, brand => brand.text.toLowerCase() === newOption.toLowerCase())
            if(!existingBrand) {
              const response = await httpService.put('my-machines/machine-models', { machineType: type, name: newOption, parentId: this.selectedType })
              const newVehicleTree = response.data
              const newBrand = _.chain(newVehicleTree).find(obj => obj.id === this.selectedType).get('brands').find(brand => brand.name === newOption).value()
              if(newBrand) {
                this.selectedBrand = newBrand.id
                this.updateVehicles(newVehicleTree)
              }
            } else{
              errorText = 'This option already exists'
            }
          }
          break
        case 'model':
          if(this.selectedType && this.selectedBrand) {
            const existingModel = _.find(this.models, model => model.text.toLowerCase() === newOption.toLowerCase())
            if(!existingModel) {
              const response = await httpService.put('my-machines/machine-models', { machineType: type, name: newOption, parentId: this.selectedBrand })
              const newVehicleTree = response.data
              const newModel = _.chain(newVehicleTree).find(obj => obj.id === this.selectedType).get('brands').find(brand => brand.id === this.selectedBrand).get('models').find(model => model.name === newOption).value()
              if(newModel) {
                this.selectedModel = newModel.id
                this.updateVehicles(newVehicleTree)
              }
            } else {
              errorText = 'This option already exists'
            }
          }
          break
        default:
          errorText = 'Unable to add option'
      }

      return errorText
    },
    stripHtml: function(html) {
      return html.replace(/<\/?[^>]+(>|$)/g, '')
    }
  }

}
</script>

<style lang="scss">
.base-vehicle-selector {
  display: flex;
  flex-direction: column;
}

.base-vehicle-selector > * {
  flex-grow: 1;
  max-width: initial;
  margin-bottom: measure(quarter);
}

.base-vehicle-selector__quick-search {
  position: relative;
}

.base-vehicle-selector__quick-search-list {
  @include reset-list;
  position: absolute;
  width: 100%;
  z-index: 4;
  background-color: color(white);
}

.base-vehicle-selector__quick-search-list:not(:empty) {
  border: 1px solid color(border-gray);
}

.base-vehicle-selector .dropdown__options {
  z-index: 4;
}
</style>
